import { Field, Form, Formik, useFormikContext } from 'formik';
import { Link } from 'react-router';
import type {
  GeoreferencePartsFragment,
  OutcropMapDataQuery,
} from '~/apollo/generated/v4/graphql';
import { FormikField } from '~/components/common/FormikField';
import { Well } from '~/components/common/Well';
import { outcropRoute } from '~/paths';
import { colorList } from '~/utils/chart';
import { rejectNil } from '~/utils/common';
import {
  distanceDisplay,
  firstOutlineOrPolygon,
  georefColors,
} from '~/utils/modules/georeference-v4';
import type { OutcropMapNearbyOutcrop } from './outcrop-georeference-map';
import { GeoreferenceMap } from './outcrop-georeference-map';
import { Heading } from '~/components/common/Heading';

type Outcrop = NonNullable<OutcropMapDataQuery['outcropGet']>;

export type VomWithOutline = {
  vom: Outcrop['virtualOutcropModels'][number];
  outline: GeoreferencePartsFragment;
};

export type OutcropMapVisibilityFormValues = {
  outcropOutline: boolean;
  voms: boolean;
  crossSections: boolean;
  sedimentaryLogs: boolean;
  gigaPans: boolean;
  nearbyOutcrops: boolean;
};

type MapField = {
  label: string;
  color: string;
};

const fields: Record<keyof OutcropMapVisibilityFormValues, MapField> = {
  outcropOutline: { label: 'Outcrop Outline', color: colorList[1] },
  voms: { label: 'Virtual Outcrop Models', color: colorList[1] },
  crossSections: { label: 'Cross Sections', color: colorList[1] },
  sedimentaryLogs: { label: 'Sedimentary Logs', color: colorList[1] },
  gigaPans: { label: 'Panoramas', color: colorList[1] },
  nearbyOutcrops: { label: 'Nearby Outcrops', color: colorList[2] },
};

export function OutcropMap({
  outcrop,
  nearbyOutcrops,
}: {
  outcrop: Outcrop;
  nearbyOutcrops: OutcropMapNearbyOutcrop[];
}) {
  const outcropId = parseInt(outcrop.id);
  const outcropOutline = firstOutlineOrPolygon(outcrop.georeferences);

  const vomsWithOutlines = outcrop.virtualOutcropModels
    .filter(vom => firstOutlineOrPolygon(vom.georeferences))
    .map(vom => {
      const outline = firstOutlineOrPolygon(vom.georeferences);
      if (!outline) return null;
      return { vom, outline };
    })
    .filter(rejectNil);
  const crossSections = outcrop.crossSections.filter(
    cs => cs.georeferences.length,
  );
  const sedLogs = outcrop.sedimentaryLogs.filter(sl => sl.georeferences.length);
  const gigaPans = outcrop.gigaPans.filter(gp => gp.georeferences.length);

  const studyCrossSections = outcrop.studies
    .flatMap(s => ({
      studyId: parseInt(s.id),
      crossSections: s.crossSections
        .filter(cs => cs.outcropTagId === outcropId)
        .filter(cs => cs.georeferences.length),
    }))
    .filter(scs => scs.crossSections.length);

  const studySedLogs = outcrop.studies
    .flatMap(s => ({
      studyId: parseInt(s.id),
      sedimentaryLogs: s.sedimentaryLogs
        .filter(sl => sl.outcropTagId === outcropId)
        .filter(sl => sl.georeferences.length),
    }))
    .filter(ssl => ssl.sedimentaryLogs.length);

  const studyGigaPans = outcrop.studies
    .flatMap(s => ({
      studyId: parseInt(s.id),
      gigaPans: s.gigaPans
        .filter(gp => gp.outcropTagId === outcropId)
        .filter(gp => gp.georeferences.length),
    }))
    .filter(sgp => sgp.gigaPans.length);

  const hasCrossSections =
    !!crossSections.length || !!studyCrossSections.length;
  const hasSedimentaryLogs = !!sedLogs.length || !!studySedLogs.length;
  const hasGigaPans = !!gigaPans.length || !!studyGigaPans.length;

  const initialValues: OutcropMapVisibilityFormValues = {
    outcropOutline: !!outcropOutline,
    voms: vomsWithOutlines.length > 0,
    crossSections: hasCrossSections,
    sedimentaryLogs: hasSedimentaryLogs,
    gigaPans: hasGigaPans,
    nearbyOutcrops: nearbyOutcrops.length > 0,
  };

  const showOutcropKey =
    !!outcropOutline ||
    vomsWithOutlines.length > 0 ||
    crossSections.length > 0 ||
    sedLogs.length > 0 ||
    gigaPans.length > 0;

  const showStudyKey =
    studyCrossSections.length > 0 ||
    studySedLogs.length > 0 ||
    studyGigaPans.length > 0;

  const showNearbyOutcropsKey = nearbyOutcrops.length > 0;

  const visibleFields = (Object.keys(fields) as (keyof typeof fields)[]).filter(
    f => initialValues[f],
  );

  if (!visibleFields.length) {
    return null;
  }

  return (
    <Formik onSubmit={() => {}} initialValues={initialValues}>
      <Form>
        <GeoreferenceMap
          outcrop={outcrop}
          outcropOutline={outcropOutline}
          vomsWithOutlines={vomsWithOutlines}
          crossSections={crossSections}
          sedimentaryLogs={sedLogs}
          gigaPans={gigaPans}
          studyCrossSections={studyCrossSections}
          studySedimentaryLogs={studySedLogs}
          studyGigaPans={studyGigaPans}
          nearbyOutcrops={nearbyOutcrops}
        />

        <Well>
          <div className="space-y-2">
            <div className="flex gap-2 justify-center">
              {visibleFields.map(field => (
                <Field
                  key={field}
                  name={field}
                  label={fields[field].label}
                  component={FormikField}
                  type="checkbox"
                  size="sm"
                />
              ))}
            </div>
            <div className="flex gap-2 items-center justify-center text-sm">
              <div className="text-xs text-muted">Key:</div>
              {showOutcropKey && (
                <div className="flex gap-1 items-center">
                  <span>Outcrop Data</span>
                  <LegendCircle color={georefColors.outcrop} />
                </div>
              )}
              {showStudyKey && (
                <div className="flex gap-1 items-center">
                  <span>Study Data</span>
                  <LegendCircle color={georefColors.study} />
                </div>
              )}
              {showNearbyOutcropsKey && (
                <div className="flex gap-1 items-center">
                  <span>Nearby Outcrops</span>
                  <LegendCircle color={colorList[2]} />
                </div>
              )}
            </div>

            <NearbyOutcropsList nearbyOutcrops={nearbyOutcrops} />
          </div>
        </Well>
      </Form>
    </Formik>
  );
}

export function LegendCircle({ color }: { color: string }) {
  return (
    <svg height="20px" width="20px">
      <circle
        cx="10px"
        cy="10px"
        r="8px"
        strokeWidth="1"
        stroke={color}
        fill={color}
        fillOpacity="60%"
      />
    </svg>
  );
}

function NearbyOutcropsList({
  nearbyOutcrops,
}: {
  nearbyOutcrops: OutcropMapNearbyOutcrop[];
}) {
  const { values } = useFormikContext<OutcropMapVisibilityFormValues>();

  if (!values.nearbyOutcrops) return null;
  if (!nearbyOutcrops.length) return null;

  return (
    <div className="p-2 bg-slate-50">
      <Heading level={4} className="pt-0">
        Nearby Outcrops
      </Heading>
      <ul className="list-disc list-inside pl-2 text-sm">
        {nearbyOutcrops.map(outcrop => (
          <li key={outcrop.id}>
            <Link to={outcropRoute(outcrop.id)} className="link">
              {outcrop.name}
            </Link>{' '}
            <span className="text-slate-500">
              ({distanceDisplay(outcrop.distanceMeters)})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
